import { AbsoluteModelPath, isModelPath, ModelPath, PathTokens } from "../../../../../../Common/ModelPath/ModelPath";
import { MatchKind } from "../../../../../../Common/ModelPath/PathMatcher";
import { CalculationFunctionDependency } from "../CalculationFunctionDependency";
import { CalculationTarget } from "../CalculationTarget";
import { CalculationFunctionExecutionPlan } from "../ExecutionPlan/CalculationFunctionExecutionPlan";
import { IterateOverChildrenPlanStep } from "../ExecutionPlan/IterateOverChildrenPlanStep";
import { PushIterationContextExecutionPlanStep } from "../ExecutionPlan/PushIterationContextExecutionPlanStep";
import { SimpleExecutionPlanStep } from "../ExecutionPlan/SimpleExecutionPlanStep";
import { ICalculationFunction } from "../ICalculationFunction";
import { IEvaluationContext } from "../IEvaluationContext";
import { ModelPathBuilder } from "../../../../../../Common/ModelPath/ModelPathBuilder";
import { AddedInstancesInfo } from "../AddedInstancesInfo";
import { Map2 } from "../../../../../../Common/ModelPath/Map/Map2";

export abstract class CalculationFunctionBase implements ICalculationFunction {
    protected readonly targetPath: AbsoluteModelPath;
    protected pathIsOutsideDependenciesListCache = new Map2<ModelPath, boolean>();
    protected hasDependencyOutsideTargetMultiplicityCache: boolean | undefined;

    protected constructor(targetPath: AbsoluteModelPath) {
        this.targetPath = targetPath;
    }

    public abstract asDebugString(): string;

    public getFullPlan(): CalculationFunctionExecutionPlan {
        const executionPlan = new CalculationFunctionExecutionPlan(this);
        const currentPrefix = new ModelPathBuilder(false);
        for (const pathPart of this.targetPath.splitByMultiplicity()) {
            if (isModelPath(pathPart)) {
                currentPrefix.append(pathPart);
            } else {
                executionPlan.addStep(new IterateOverChildrenPlanStep(currentPrefix.toAbsolutePath()));
                currentPrefix.append(pathPart);
            }
        }
        executionPlan.addStep(new SimpleExecutionPlanStep());
        return executionPlan;
    }

    public getPlanForTarget(
        targetPath2: ModelPath,
        iterationData?: AddedInstancesInfo,
        sync?: boolean
    ): CalculationFunctionExecutionPlan {
        const executionPlan = new CalculationFunctionExecutionPlan(this);
        const currentPrefix = new ModelPathBuilder(false);
        const pathMatching = this.targetPath.matchFor(targetPath2);
        for (const match of pathMatching) {
            if (match.kind === MatchKind.MatchedPath) {
                currentPrefix.append(match.prefix);
            } else if (match.kind === MatchKind.MatchedMultiplicity) {
                if (PathTokens.isMultiToken(match.source) && PathTokens.isMultiToken(match.target)) {
                    const currentPrefixPath = currentPrefix.toAbsolutePath();
                    executionPlan.addStep(
                        new IterateOverChildrenPlanStep(
                            currentPrefixPath,
                            iterationData != undefined && currentPrefixPath.isEquals(iterationData.path)
                                ? iterationData.instancesToIterate
                                : undefined,
                            sync
                        )
                    );
                    currentPrefix.append(match.source);
                } else if (PathTokens.isSimpleToken(match.source) && PathTokens.isMultiToken(match.target)) {
                    currentPrefix.append(match.source);
                } else if (PathTokens.isMultiToken(match.source) && PathTokens.isSimpleToken(match.target)) {
                    executionPlan.addStep(
                        new PushIterationContextExecutionPlanStep(currentPrefix.toPath(), match.target)
                    );
                    currentPrefix.append(match.source);
                }
            } else {
                throw new Error(
                    `Cannot build execution plan of function with target '${this.targetPath.toString()}' to evaluate '${targetPath2.toString()}'`
                );
            }
        }
        executionPlan.addStep(new SimpleExecutionPlanStep());
        return executionPlan;
    }

    public pathIsOutsideDependenciesList(path: ModelPath): boolean {
        return this.pathIsOutsideDependenciesListCache.getOrSet(path, () =>
            Iterator.from(this.getDependencies())
                .map(x => x.getDependencyPath())
                .every(dependencyPath => {
                    const matching = dependencyPath.matchFor(path);
                    return (
                        dependencyPath.isContainCurrentIteration() &&
                        matching.find(matchingPart => matchingPart.kind === MatchKind.DifferentSuffix) != undefined
                    );
                })
        );
    }

    public abstract execute(context: IEvaluationContext): void | Promise<void>;

    public abstract getDependencies(): readonly CalculationFunctionDependency[];

    public abstract getTarget(): CalculationTarget;

    public abstract isDependsOn(modelPath: CalculationTarget): boolean;
}
